import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Eye, Plus, Trash2 } from 'lucide-react';
import { api } from '../../api';
import { pode, useSessao } from '../../sessao';
import { Modal } from '../../componentes/Modal';
import { tiposDe } from '../../componentes/formatos';
import { CamposItem, ITEM_VAZIO, Previa, corpoItem, type CampoItem } from './Checklists';
import { NOME_ACAO_AJUSTE, type Ajuste, type Bloco } from './comum';

/**
 * Checklist deste cliente: os ajustes (esconder/obrigar/dispensar/acrescentar) por cima da biblioteca.
 * Ajuste no local vence o do cliente. Sempre com motivo — é o que explica, meses depois, por que o checklist daqui é diferente.
 */
export function AjustesCliente({ clienteId, clienteNome, locais }: { clienteId: string; clienteNome: string; locais: { id: string; nome: string; ativo: boolean }[] }) {
  const { usuario } = useSessao();
  const podeAjustar = pode(usuario, 'checklist.ajustar');
  const podeVer = pode(usuario, 'checklist.ver');
  const [ajustes, setAjustes] = useState<Ajuste[] | null>(null);
  const [modal, setModal] = useState<'novo' | 'previa' | null>(null);
  const [tipos, setTipos] = useState<{ id: string; nome: string; natureza: string }[]>([]);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(() => { api<Ajuste[]>(`/checklists/ajustes?cliente_id=${clienteId}`).then(setAjustes).catch((e) => setErro(e.message)); }, [clienteId]);
  useEffect(() => { if (podeVer) carregar(); }, [carregar, podeVer]);
  useEffect(() => { if (modal === 'previa' && !tipos.length) api<typeof tipos>('/tipos-os').then(setTipos).catch(() => {}); }, [modal, tipos.length]);
  if (!podeVer) return null;

  async function remover(a: Ajuste) {
    if (!window.confirm(`Remover o ajuste "${NOME_ACAO_AJUSTE[a.acao]}: ${a.item_texto ?? a.item_codigo}"? OS que já começaram não mudam.`)) return;
    try { await api(`/checklists/ajustes/${a.id}`, { metodo: 'DELETE' }); carregar(); } catch (e) { setErro((e as Error).message); }
  }

  return (
    <section className="secao">
      <div className="secao-topo">
        <h2>Checklist deste cliente <span className="contador">{ajustes?.length ?? 0}</span></h2>
        <div className="acoes-cabecalho">
          <button className="btn-fantasma" onClick={() => setModal('previa')}><Eye size={16} aria-hidden /> Ver como fica</button>
          {podeAjustar && <button className="btn-fantasma" onClick={() => setModal('novo')}><Plus size={16} aria-hidden /> Ajuste</button>}
        </div>
      </div>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      {!ajustes ? null : ajustes.length === 0 ? (
        <p className="nota">Sem ajustes: as OS daqui usam o checklist padrão, repetido para cada equipamento cadastrado no parque.</p>
      ) : (
        <div className="cartao tabela-cartao">
          <table className="tabela">
            <thead><tr><th>Onde</th><th>Bloco</th><th>Ajuste</th><th>Motivo</th>{podeAjustar && <th aria-label="Ações" />}</tr></thead>
            <tbody>
              {ajustes.map((a) => (
                <tr key={a.id}>
                  <td>{a.local ?? 'Cliente inteiro'}</td>
                  <td>{a.bloco}</td>
                  <td><strong>{NOME_ACAO_AJUSTE[a.acao]}</strong><small>{a.item_texto ?? a.item_codigo}</small></td>
                  <td>{a.motivo}<small>{a.criado_por ?? ''} · {new Date(a.criado_em).toLocaleDateString('pt-BR')}</small></td>
                  {podeAjustar && <td className="acoes"><button className="btn-icone" onClick={() => remover(a)} aria-label="Remover ajuste"><Trash2 size={16} /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal titulo="Ajuste de checklist" aberto={modal === 'novo'} aoFechar={() => setModal(null)}>
        {modal === 'novo' && <FormAjuste clienteId={clienteId} locais={locais.filter((l) => l.ativo)} aoSalvar={() => { setModal(null); carregar(); }} aoCancelar={() => setModal(null)} />}
      </Modal>
      <Modal titulo="Como fica numa OS" aberto={modal === 'previa'} aoFechar={() => setModal(null)} classe="modal-largo">
        {modal === 'previa' && <Previa tipos={tipos} localInicial={{ clienteId, clienteNome }} />}
      </Modal>
    </section>
  );
}

function FormAjuste({ clienteId, locais, aoSalvar, aoCancelar }: { clienteId: string; locais: { id: string; nome: string }[]; aoSalvar: () => void; aoCancelar: () => void }) {
  const [blocos, setBlocos] = useState<Bloco[]>([]);
  const [f, setF] = useState({ onde: '', bloco_id: '', acao: 'esconder', item_codigo: '', motivo: '' });
  const [item, setItem] = useState<CampoItem>(ITEM_VAZIO);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  useEffect(() => { api<Bloco[]>('/checklists').then((b) => setBlocos(b.filter((x) => x.ativo))).catch(() => {}); }, []);
  const bloco = blocos.find((b) => b.id === f.bloco_id);

  async function enviar(e: FormEvent) {
    e.preventDefault(); setErro(null); setSalvando(true);
    try {
      await api('/checklists/ajustes', { metodo: 'POST', corpo: {
        ...(f.onde ? { local_id: f.onde } : { cliente_id: clienteId }), bloco_id: f.bloco_id, acao: f.acao, motivo: f.motivo.trim(),
        ...(f.acao === 'acrescentar' ? { item_novo: corpoItem(item) } : { item_codigo: f.item_codigo }),
      } });
      aoSalvar();
    } catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível salvar.'); }
    finally { setSalvando(false); }
  }

  return (
    <form className="form" onSubmit={enviar}>
      <div className="linha-2">
        <label>Vale para<select value={f.onde} onChange={set('onde')}><option value="">O cliente inteiro</option>{locais.map((l) => <option key={l.id} value={l.id}>Só {l.nome}</option>)}</select></label>
        <label>Bloco<select required value={f.bloco_id} onChange={(e) => setF((x) => ({ ...x, bloco_id: e.target.value, item_codigo: '' }))}>
          <option value="">Escolha</option>{blocos.map((b) => <option key={b.id} value={b.id}>{b.nome}{b.tipo_os ? ` (${b.tipo_os})` : ''}</option>)}</select></label>
      </div>
      <label>O que fazer<select value={f.acao} onChange={set('acao')}>{tiposDe(NOME_ACAO_AJUSTE).map(([v, r]) => <option key={v} value={v}>{r}</option>)}</select></label>
      {f.acao === 'acrescentar' ? <CamposItem f={item} setF={setItem} /> : (
        <label>Item<select required value={f.item_codigo} onChange={set('item_codigo')} disabled={!bloco}>
          <option value="">{bloco ? 'Escolha' : 'Escolha o bloco primeiro'}</option>
          {bloco?.itens.filter((i) => i.ativo).map((i) => <option key={i.codigo} value={i.codigo}>{i.texto}{i.obrigatorio ? '' : ' (opcional)'}</option>)}
        </select></label>
      )}
      <label>Motivo<input required minLength={5} maxLength={500} value={f.motivo} onChange={set('motivo')} placeholder="Ex.: contrato não cobre lubrificação; portaria P2 tem fotocélula" /></label>
      <p className="nota">Vale para OS que ainda não começaram. Ajuste no parque vence o do cliente.</p>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      <div className="acoes-form">
        <button type="button" className="btn-fantasma" onClick={aoCancelar}>Cancelar</button>
        <button className="btn" disabled={salvando || !f.bloco_id || f.motivo.trim().length < 5}>{salvando ? 'Salvando…' : 'Salvar ajuste'}</button>
      </div>
    </form>
  );
}
